import React, { useEffect, useState, useCallback } from "react";
import { db } from "../firebase/firebase";
import { collection, getDocs } from "firebase/firestore";
import RideHailingReport from "./driverReports/RideHailingReport";
import ManualTripsReport from "./driverReports/ManualTripsReport";
import CarWiseStatementUI from "./driverReports/CarWiseStatementUI";
import POSSummaryReport from "./driverReports/POSSummaryReport";

export default function DriversStatement() {
  const [rideHailingTabs, setRideHailingTabs] = useState([]);
  const [manualTabs, setManualTabs] = useState([]);
  const [manualClientsMap, setManualClientsMap] = useState({});
  const [loadingPlatforms, setLoadingPlatforms] = useState(false);

  const [activeTab, setActiveTab] = useState("");
  const [activeType, setActiveType] = useState(""); // "ride" | "manual" | "car" | "pos"

  /* 1️⃣ RIDE-HAILING PLATFORMS - OPTIMIZED WITH PARALLEL PROCESSING */
  const loadRideHailingCompanies = useCallback(async () => {
    setLoadingPlatforms(true);
    try {
      console.log("🔍 Loading ride-hailing platforms (optimized)...");
      
      // First, try to load from cache/localStorage for faster loading
      const cachedPlatforms = localStorage.getItem("rideHailingPlatforms");
      const cacheTimestamp = localStorage.getItem("platformsCacheTimestamp");
      
      // Use cache if it's less than 5 minutes old
      if (cachedPlatforms && cacheTimestamp) {
        const cacheAge = Date.now() - parseInt(cacheTimestamp);
        if (cacheAge < 5 * 60 * 1000) { // 5 minutes
          const platforms = JSON.parse(cachedPlatforms);
          console.log("🚕 Loaded from cache:", platforms);
          setRideHailingTabs(platforms);
          
          // Auto-select first platform
          if (platforms.length > 0 && !activeTab && !activeType) {
            setActiveTab(platforms[0]);
            setActiveType("ride");
          }
          setLoadingPlatforms(false);
          return;
        }
      }
      
      // If no cache or cache expired, load from Firestore
      const driversSnap = await getDocs(collection(db, "driverWeeklyEarnings"));
      const platformsSet = new Set();
      
      console.log(`📋 Found ${driversSnap.size} drivers, processing in parallel...`);
      
      // Process in parallel batches for maximum speed
      const batchSize = 10; // Process 10 drivers at a time (increased for speed)
      const driverDocs = driversSnap.docs;
      
      // Create all promises at once for maximum parallelization
      const allPromises = [];
      
      for (let i = 0; i < driverDocs.length; i += batchSize) {
        const batch = driverDocs.slice(i, i + batchSize);
        
        // Process each batch in parallel
        const batchPromises = batch.map(async (driverDoc) => {
          try {
            const driverId = driverDoc.id;
            const uploadsRef = collection(db, "driverWeeklyEarnings", driverId, "uploads");
            const uploadsSnap = await getDocs(uploadsRef);
            
            const batchPlatforms = new Set();
            uploadsSnap.forEach((uploadDoc) => {
              const data = uploadDoc.data();
              console.log("🔥 Firestore Upload Found:", data.platform);

              const raw = data.platform || "";
const val = raw.toString().trim().toLowerCase();
if (val) batchPlatforms.add(val);

            });
            
            return Array.from(batchPlatforms);
          } catch (error) {
            console.error(`Error processing driver ${driverDoc.id}:`, error);
            return [];
          }
        });
        
        allPromises.push(...batchPromises);
      }
      
      // Wait for ALL promises to complete at once
      const allResults = await Promise.all(allPromises);
      
      // Add all platforms to the main set
      allResults.forEach((platforms) => {
        platforms.forEach(platform => platformsSet.add(platform));
      });
      
      const platforms = Array.from(platformsSet).sort();
      console.log("🚕 Loaded from Firestore:", platforms.length, "platforms");
      setRideHailingTabs(platforms);
      
      // Cache the results for next time
      localStorage.setItem("rideHailingPlatforms", JSON.stringify(platforms));
      localStorage.setItem("platformsCacheTimestamp", Date.now().toString());
      
      // Auto-select first platform
      if (platforms.length > 0 && !activeTab && !activeType) {
        setActiveTab(platforms[0]);
        setActiveType("ride");
      }
      
    } catch (err) {
      console.error("Error loading ride-hailing companies:", err);
    } finally {
      setLoadingPlatforms(false);
    }
  }, [activeTab, activeType]);

  /* 2️⃣ MANUAL TRIP COMPANIES */
  const loadManualTripCompanies = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, "clients"));
      const labels = [];
      const map = {};

      snap.docs.forEach((d) => {
        const data = d.data();
        const name = data.name;
        if (name) {
          const label = name.toUpperCase();
          labels.push(label);
          map[label] = d.id;
        }
      });

      labels.sort();
      setManualTabs(labels);
      setManualClientsMap(map);
    } catch (err) {
      console.error("Error loading manual trip companies:", err);
    }
  }, []);

  useEffect(() => {
    loadRideHailingCompanies();
    loadManualTripCompanies();
  }, [loadRideHailingCompanies, loadManualTripCompanies]);

  const isRideHailingTab = (label) => rideHailingTabs.includes(label);
  const isManualTab = (label) => manualTabs.includes(label);

  return (
    <div style={{ padding: 20 }}>
      <h2>📄 Driver Wise Income Summary</h2>

      {/* MAIN TABS BLOCK */}
      <div
        style={{
          marginTop: 20,
          padding: 16,
          background: "#f0f4ff",
          borderRadius: 12,
          border: "1px solid #cdd3eb",
        }}
      >
        <h3 style={{ marginBottom: 12 }}>Company Reports</h3>

        {/* RIDE-HAILING TABS */}
        <div style={{ marginBottom: 6, fontWeight: "bold" }}>
          🚕 Ride-Hailing Platforms
          {loadingPlatforms && (
            <span style={{ marginLeft: 10, fontSize: 12, color: "#666", fontWeight: "normal" }}>
              (Loading...)
            </span>
          )}
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", minHeight: "40px" }}>
          {loadingPlatforms ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: "16px", height: "16px", border: "2px solid #ccc", borderTop: "2px solid #0d47a1", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
              <span style={{ fontSize: 12, color: "#666" }}>
                Loading platforms from database...
              </span>
            </div>
          ) : rideHailingTabs.length === 0 ? (
            <span style={{ fontSize: 12, color: "#666" }}>
              No ride-hailing platforms found.
            </span>
          ) : (
            rideHailingTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  setActiveType("ride");
                }}
                style={{
                  padding: "8px 14px",
                  background: activeTab === tab && activeType === "ride" ? "#0d47a1" : "#e3e9ff",
                  color: activeTab === tab && activeType === "ride" ? "#fff" : "#000",
                  borderRadius: 6,
                  border: "1px solid #a6b3d8",
                  cursor: "pointer",
                  fontWeight: 600,
                  transition: "all 0.2s",
                }}
              >
                {tab}
              </button>
            ))
          )}
        </div>

        {/* MANUAL TRIP TABS */}
        <div style={{ marginTop: 20, marginBottom: 6, fontWeight: "bold" }}>
          📦 Manual Trips Companies
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {manualTabs.length === 0 && (
            <span style={{ fontSize: 12, color: "#666" }}>
              No clients found.
            </span>
          )}

          {manualTabs.map((tab) => (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setActiveType("manual");
              }}
              style={{
                padding: "8px 14px",
                background:
                  activeTab === tab && activeType === "manual" ? "#0d47a1" : "#e3e9ff",
                color:
                  activeTab === tab && activeType === "manual" ? "#fff" : "#000",
                borderRadius: 6,
                border: "1px solid #a6b3d8",
                cursor: "pointer",
                fontWeight: 600,
                transition: "all 0.2s",
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* 🆕 POS SUMMARY TAB */}
        <div style={{ marginTop: 20, marginBottom: 6, fontWeight: "bold" }}>
          💳 POS Transactions Summary
        </div>

        <button
          onClick={() => {
            setActiveTab("POS_SUMMARY");
            setActiveType("pos");
          }}
          style={{
            padding: "8px 14px",
            background:
              activeTab === "POS_SUMMARY" && activeType === "pos"
                ? "#0d47a1"
                : "#e3e9ff",
            color:
              activeTab === "POS_SUMMARY" && activeType === "pos"
                ? "#fff"
                : "#000",
            borderRadius: 6,
            border: "1px solid #a6b3d8",
            cursor: "pointer",
            fontWeight: 600,
            transition: "all 0.2s",
          }}
        >
          POS Summary
        </button>

        {/* 🔥 CAR-WISE STATEMENT TAB */}
        <div style={{ marginTop: 20, marginBottom: 6, fontWeight: "bold" }}>
          🚗 Car-Wise Monthly Driver Statements
        </div>

        <button
          onClick={() => {
            setActiveTab("CAR_STATEMENT");
            setActiveType("car");
          }}
          style={{
            padding: "8px 14px",
            background:
              activeTab === "CAR_STATEMENT" && activeType === "car"
                ? "#0d47a1"
                : "#e3e9ff",
            color:
              activeTab === "CAR_STATEMENT" && activeType === "car"
                ? "#fff"
                : "#000",
            borderRadius: 6,
            border: "1px solid #a6b3d8",
            cursor: "pointer",
            fontWeight: 600,
            letterSpacing: 0.3,
            transition: "all 0.2s",
          }}
        >
          Car Statement
        </button>
      </div>

      {/* CONTENT PANEL - UPDATED */}
      <div
        style={{
          marginTop: 25,
          padding: 20,
          background: "#fff",
          border: "1px solid #ddd",
          borderRadius: 12,
        }}
      >
        {!activeTab && <p>Select a company/report tab above to view details.</p>}

        {/* TITLE AREA - UPDATED FOR ALL TYPES */}
        {activeTab && (
          <h3>
            📌 Showing Report for:{" "}
            <u>
              {activeType === "ride" && activeTab}
              {activeType === "manual" && activeTab}
              {activeType === "car" && "Car-Wise Monthly Driver Statements"}
              {activeType === "pos" && "POS Transactions Summary"}
            </u>
          </h3>
        )}

        {/* CONTENT RENDERING - UPDATED WITH POS */}
        <div style={{ marginTop: 20 }}>
          {activeType === "ride" && isRideHailingTab(activeTab) && (
            <RideHailingReport platform={activeTab} />
          )}

          {activeType === "manual" && isManualTab(activeTab) && (
            <ManualTripsReport clientId={manualClientsMap[activeTab]} />
          )}

          {/* 🔥 SHOW CAR-WISE STATEMENT */}
          {activeType === "car" && <CarWiseStatementUI />}

          {/* 🆕 SHOW POS SUMMARY REPORT */}
          {activeType === "pos" && <POSSummaryReport />}
        </div>
      </div>

      {/* Add CSS for spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}